import { FilterEquipError } from '../../Service/Exception/FilterEquipError';
import { CallbackFilter } from '../CallbackFilter';
import { ContainsFilter } from '../ContainsFilter';
import { EqualsFilter, EqualsFilterValueType } from '../EqualsFilter';
import { FilterInterface, FilterType } from '../FilterInterface';
import { OneOfFilter, OneOfFilterValueType } from '../OneOFilter';
import { RangeFilterValueType } from '../RangeFilter';
import { RangeFilterValue } from '../RangeFilterValue';
import { FilterDeserializerResult } from './FilterDeserializerResult';
import { FilterValueType } from './FilterEquipService';
import { separator } from './SerializationConstants';

export class FilterDeserializer {
    /**
     * Deserialize the value of the given Filter if the search parameters contain a value for the Filter's keyPath
     *
     * @param {FilterInterface<T>} filter
     * @param {URLSearchParams} searchParams
     * @return {FilterDeserializerResult<T>}
     */
    public deserializeFilterState<T extends FilterValueType>(
        filter: FilterInterface<T>,
        searchParams: URLSearchParams
    ): FilterDeserializerResult<T | undefined> {
        const parameter = this.getParameterForFilter(searchParams, filter);
        if (parameter) {
            return FilterDeserializerResult.some(this.prepareValueForFilter(filter, parameter));
        } else {
            return FilterDeserializerResult.none();
        }
    }

    private getParameterForFilter(
        searchParams: URLSearchParams,
        filter: FilterInterface<FilterValueType>
    ): string | null {
        return searchParams.get(filter.keyPath);
    }

    private prepareValueForFilter<T extends FilterValueType>(
        filter: FilterInterface<T>,
        parameter: string
    ): T {
        switch (filter.type) {
            case FilterType.Equals:
                // filter = EqualsFilter<EqualsFilterValueType>
                return this.prepareEqualsFilterValue(filter as EqualsFilter<any>, parameter) as T;
            case FilterType.Contains:
                // filter = ContainsFilter<EqualsFilterValueType>
                return this.prepareEqualsFilterValue(filter as ContainsFilter<any>, parameter) as T;
            case FilterType.OneOf:
                // filter = OneOfFilter<OneOfFilterValueType>
                return this.prepareOneOfFilterValueType(filter as OneOfFilter<any>, parameter) as T;
            case FilterType.Range:
                // filter = RangeFilter<RangeFilterValueType>
                return this.prepareRangeFilterValueType(parameter) as T;
            case FilterType.Callback:
                // filter = CallbackFilter
                return this.prepareCallbackFilterValueType(filter as CallbackFilter<any>, parameter) as T;
        }
    }

    private prepareEqualsFilterValue<T extends EqualsFilterValueType>(
        filter: EqualsFilter<T> | ContainsFilter<T>,
        parameter: string
    ): EqualsFilterValueType {
        if (typeof filter.value === 'number') {
            return parseFloat(parameter);
        } else {
            return parameter;
        }
    }

    private prepareOneOfFilterValueType<T extends OneOfFilterValueType>(
        filter: OneOfFilter<T>,
        parameter: string
    ): OneOfFilterValueType {
        const useNumber = filter.value.length > 0 && filter.value[0] === 'number';
        const split = parameter.split(separator);

        if (useNumber) {
            return split.map(p => parseFloat(p));
        } else {
            return split;
        }
    }

    private prepareRangeFilterValueType(parameter: string): RangeFilterValueType {
        const split = parameter.split(separator);
        if (split.length < 2) {
            throw new FilterEquipError(
                `Range filter value must be formatted like "min${separator}max" (e.g. "10${separator}290")`
            );
        }

        const min = parseFloat(split[0]);
        if (isNaN(min)) {
            throw new FilterEquipError('Range filter min value must be a valid number');
        }
        const max = parseFloat(split[1]);
        if (isNaN(max)) {
            throw new FilterEquipError('Range filter max value must be a valid number');
        }

        return new RangeFilterValue(min, max);
    }

    private prepareCallbackFilterValueType<T>(filter: CallbackFilter<T>, parameter: string) {
        const oldValue = filter.value;
        if (oldValue instanceof RangeFilterValue) {
            return this.prepareRangeFilterValueType(parameter);
        }
        if (Array.isArray(oldValue)) {
            return parameter.split(separator);
        }
        if (typeof oldValue === 'number') {
            return parseFloat(parameter);
        }

        return parameter;
    }
}
